import React, { useRef, useState } from "react";
import PropTypes from "prop-types";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Box,
  ButtonBase,
  CardContent,
  ClickAwayListener,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";

// project imports
import SettingTab from "./SettingTab";
import MainCard from "../../../../../shared/common/MainCard";
import Transitions from "../../../../../shared/common/@extended/Transitions";
import IconButton from "../../../../../shared/common/@extended/IconButton";

// assets
import LogoutOutlined from "@ant-design/icons/LogoutOutlined";
import avatarimage from "../../../../../../assets/images/users/profile_vector.png";
import { useStore } from "../../../../../stores/store";
import { observer } from "mobx-react-lite";
import { Navigate, useNavigate } from "react-router-dom";
import { getProfile } from "../../../../../../app/services/AuthServices";

// tab panel wrapper
function TabPanel({ children, value, index, ...other }: any) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}


// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { userStore } = useStore();
  const { user } = userStore;
  const theme = useTheme();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  if (!user) return <Navigate to="/login" replace />;

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: any) => {
    //@ts-ignore
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleLogout = () => {
    userStore.logout(); // only clears state + token
    navigate("/login"); // handle side effect separately
  };

  const handleViewProfile = async () => {
    try {
      setLoadingProfile(true);
      setOpenDialog(true);
      const response = await getProfile();
      setProfileData(response.data);
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoadingProfile(false);
    }
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.85 }}>
      <ButtonBase
        sx={(theme) => ({
          p: 0.25,
          bgcolor: open ? "grey.100" : "transparent",
          borderRadius: 1,
          "&:hover": { bgcolor: "secondary.lighter" },
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2,
          },
          ...theme.applyStyles("dark", {
            bgcolor: open ? "background.default" : "transparent",
            "&:hover": { bgcolor: "secondary.light" },
          }),
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" sx={{ gap: 1.25, alignItems: "center", p: 0.5 }}>
          <Typography variant="subtitle1" sx={{ textTransform: "capitalize" }}>
            {user.name || "Default"}
          </Typography>
          <Box
            component="img"
            src={avatarimage}
            alt="Custom"
            sx={{ width: 10, height: 5, borderRadius: "50%" }}
          />
        </Stack>
      </ButtonBase>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 9],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          //@ts-ignore
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            {/* @ts-ignore */}
            <Paper
              sx={(theme) => ({
                boxShadow: theme.shadows[1],
                width: 290,
                minWidth: 240,
                maxWidth: { xs: 250, md: 290 },
              })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                {/* @ts-ignore */}
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid>
                        <Stack direction="row" sx={{ gap: 1.25, alignItems: "center" }}>
                          <Stack>
                            <Typography variant="h6">
                              {user.name || "Default"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.role.name || "Default"}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid>
                        <Tooltip title="Logout">
                          <IconButton
                            size="large"
                            sx={{ color: "text.primary" }}
                            onClick={handleLogout}
                          >
                            <LogoutOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <CardContent>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Typography
                        variant="h5"
                        sx={{ cursor: "pointer", color: "primary.main" }}
                        onClick={handleViewProfile}
                      >
                        View Profile
                      </Typography>
                    </Box>
                  </CardContent>

                  {/* <TabPanel value={value} index={0} dir={theme.direction}>
                    <ProfileTab />
                  </TabPanel> */}
                  <TabPanel  index={1} dir={theme.direction}>
                    <SettingTab />
                  </TabPanel>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      {/* Profile Popup Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>User Profile</DialogTitle>
        <DialogContent dividers>
          {loadingProfile ? (
            <CircularProgress />
          ) : profileData ? (
            <Box>
              <Typography variant="subtitle1">
                <strong>Name:</strong> {profileData.name}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Email:</strong> {profileData.email}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Role:</strong> {profileData.role?.name}
              </Typography>
              {/* Add more fields if needed */}
            </Box>
          ) : (
            <Typography>No profile data found.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number,
  index: PropTypes.number,
  other: PropTypes.any,
};

export default observer(Profile);
